import {
  Group,
  Text,
  useMantineTheme,
  rem,
  BackgroundImage,
  ThemeIcon,
  Overlay,
  AspectRatio,
} from "@mantine/core"
import { IconUpload, IconPhoto, IconX } from "@tabler/icons-react"
import { Dropzone, type DropzoneProps, IMAGE_MIME_TYPE, type FileWithPath } from "@mantine/dropzone"
import React, { useMemo } from "react"
import { API_URL } from "../../config"

// ----------------------------------------------------------------------

type Props = {
  formValues: {
    onChange: (value: FileWithPath | undefined) => void
    value: string | FileWithPath | undefined
  }
} & Partial<DropzoneProps>

export function ImageDropzone({ formValues, ...rest }: Props): React.JSX.Element {
  const theme = useMantineTheme()

  const imageURL = useMemo(() => {
    if (formValues.value) {
      return formValues.value instanceof File
        ? URL.createObjectURL(formValues.value)
        : `${API_URL}/media/${formValues.value}`
    }

    return undefined
  }, [ formValues.value ])

  const resetImage = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    formValues.onChange(undefined)
  }

  const onDrop = (files: Array<FileWithPath>) => {
    formValues.onChange(files[0])
  }

  const onLoad = () => {
    if (formValues.value instanceof File && imageURL) {
      URL.revokeObjectURL(imageURL)
    }
  }

  return (
    <Dropzone
      onDrop={onDrop}
      maxSize={10 * 1024 ** 2}
      maxFiles={1}
      accept={IMAGE_MIME_TYPE}
      {...rest}
    >
      {imageURL ? (
          <AspectRatio ratio={16 / 9} mx="auto">
            <BackgroundImage
              sx={{ backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" }}
              src={imageURL}
              onLoad={onLoad}
            >
              <Overlay opacity={0.2} center style={{ pointerEvents: "all", userSelect: "auto" }}>
                <ThemeIcon onClick={resetImage} variant="outline" radius="xl" size="xl" color="red">
                  <IconX/>
                </ThemeIcon>
              </Overlay>
            </BackgroundImage>
          </AspectRatio>
        ) :
        (
          <Group position="center" spacing="xl" style={{ minHeight: rem(220) }}>
            <Dropzone.Accept>
              <IconUpload
                size="3.2rem"
                stroke={1.5}
                color={theme.colors[theme.primaryColor][theme.colorScheme === "dark" ? 4 : 6]}
              />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX
                size="3.2rem"
                stroke={1.5}
                color={theme.colors.red[theme.colorScheme === "dark" ? 4 : 6]}
              />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconPhoto size="3.2rem" stroke={1.5}/>
            </Dropzone.Idle>

            <div>
              <Text size="xl" inline>
                Перетащите сюда изображение или нажмите для выбора файла
              </Text>
              <Text size="sm" color="dimmed" inline mt={7}>
                Допустимо только одно изображение, размер файла не должен превышать 10мб
              </Text>
            </div>
          </Group>
        )
      }
    </Dropzone>
  )
}
